// What the build line shows, derived only from the display VM's typed fields and the clock.
// No message text is parsed, no step is invented for an outside agent, and "done" needs the
// saved version: a reveal never stands in for proof.
import type { BuildExperienceCopyKey } from '@/i18n/ns/build-experience';
import type { BuildLineVM, BuildPhase, BuildSource, C4BlockedReason, DisplayAction } from './vm';

/** The steps the line counts ("2 of 5"). Queued and the workspace beat come before them. */
export const COUNTED: readonly BuildPhase[] = ['authoring', 'compiling', 'rendering', 'checking', 'saving'];

export const PHASE_COPY: Record<BuildPhase, BuildExperienceCopyKey> = {
  queued: 'phaseQueued',
  acquiring: 'phaseAcquiring',
  authoring: 'phaseAuthoring',
  compiling: 'phaseCompiling',
  rendering: 'phaseRendering',
  checking: 'phaseChecking',
  saving: 'phaseSaving',
  settled: 'phaseSettled',
};

const BLOCKED_COPY: Record<NonNullable<C4BlockedReason>, BuildExperienceCopyKey> = {
  approval: 'blockedApproval',
  budget: 'blockedBudget',
  provider: 'blockedProvider',
  viewer_context_required: 'blockedViewer',
};

/** Silence beats (F5 rubric): an honest "still working" at 8 s, a reason and a choice at 30 s. */
export const STILL_MS = 8_000;
export const SLOW_MS = 30_000;
export type Beat = 'live' | 'still' | 'slow';

export type LineStatus =
  | 'loading'
  | 'denied'
  | 'empty'
  | 'running'
  | 'stopping'
  | 'confirming'
  | 'blocked'
  | 'failed'
  | 'stopped'
  | 'done'
  | 'unknown';

export type StepState = 'done' | 'current' | 'pending';

export type LineView = {
  status: LineStatus;
  phase: BuildPhase | null;
  /** The counted steps with their state; empty before authoring starts. */
  steps: { phase: BuildPhase; state: StepState }[];
  /** 1-based position among the counted steps, or null before the first one. */
  position: number | null;
  beat: Beat;
  /** Milliseconds since the last thing the build reported (or since it started). */
  quietMs: number;
  elapsedMs: number | null;
  remote: boolean;
  blockedCopy: BuildExperienceCopyKey | null;
  /** Actions the person can see, in the VM's order, minus the ones the line places itself. */
  actions: DisplayAction[];
  stop: DisplayAction | null;
};

const parse = (at: string | null | undefined) => {
  if (!at) return null;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? ms : null;
};

export function beatFor(quietMs: number): Beat {
  if (quietMs >= SLOW_MS) return 'slow';
  if (quietMs >= STILL_MS) return 'still';
  return 'live';
}

export function deriveLine(vm: BuildLineVM, source: BuildSource | null, now: number): LineView {
  const remote = source?.origin === 'mcp' || source?.origin === 'terminal';
  const started = parse(source?.startedAt);
  const elapsedMs = started === null ? null : Math.max(0, now - started);
  const stop = vm.actions.find((a) => a.name === 'stop') ?? null;
  const actions = vm.actions.filter((a) => a.name !== 'stop');
  const base: LineView = {
    status: 'loading',
    phase: null,
    steps: [],
    position: null,
    beat: 'live',
    quietMs: 0,
    elapsedMs,
    remote,
    blockedCopy: null,
    actions,
    stop,
  };
  const state = vm.state;
  switch (state.status) {
    case 'loading':
      return base;
    case 'denied':
      return { ...base, status: 'denied', stop: null, actions: [] };
    case 'empty':
      return { ...base, status: 'empty' };
    case 'blocked':
      return {
        ...base,
        status: 'blocked',
        blockedCopy: state.blockedReason ? BLOCKED_COPY[state.blockedReason] : 'blockedOther',
      };
    case 'error':
      // pending-contract: ART projects a cancelled job as error code 'cancelled'.
      return { ...base, status: state.code === 'cancelled' ? 'stopped' : 'failed', stop: null };
    case 'ready':
      break;
  }
  const data = state.data;
  const index = COUNTED.indexOf(data.phase);
  const settled = data.phase === 'settled';
  const steps = COUNTED.map((phase, i) => ({
    phase,
    state: (settled || (index >= 0 && i < index) ? 'done' : i === index ? 'current' : 'pending') as StepState,
  }));
  const lastSeen = parse(data.lastSeenAt) ?? started;
  const quietMs = lastSeen === null ? 0 : Math.max(0, now - lastSeen);
  let status: LineStatus = 'running';
  if (settled) {
    // Done needs the saved version. Settled but still pending is confirming, not a doubt;
    // only an unknown outcome (or confirmed without a saved version) says it couldn't confirm.
    if (data.outcome === 'confirmed' && data.savedVersionRef) status = 'done';
    else if (data.outcome === 'pending') status = 'confirming';
    else status = 'unknown';
  } else if (stop?.status === 'pending') status = 'stopping';
  return {
    ...base,
    status,
    phase: data.phase,
    steps: index >= 0 || settled ? steps : [],
    position: index >= 0 ? index + 1 : settled ? COUNTED.length : null,
    quietMs,
    beat: status === 'running' ? beatFor(quietMs) : 'live',
    // An unknown outcome still offers Stop here; a confirmed or confirming one doesn't.
    stop: settled && status !== 'unknown' ? null : stop,
  };
}

/**
 * "Last step 12 s ago" for an outside agent, from its own reports only. Null before it has
 * reported anything: the line then says so instead of narrating.
 */
export function lastStepParts(quietMs: number, hasReported: boolean): { key: BuildExperienceCopyKey; values: Record<string, string> } {
  if (!hasReported) return { key: 'remoteNoStep', values: {} };
  const seconds = Math.floor(quietMs / 1000);
  if (seconds < 60) return { key: 'remoteLastSecond', values: { seconds: String(seconds) } };
  return { key: 'remoteLastMinute', values: { minutes: String(Math.floor(seconds / 60)) } };
}

/** m:ss, the clock beside the line. Time passing is information, so it ticks under reduced motion too. */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
